<div class="page-container" [class.loading-overlay]="loading()">
  <!-- Page Header -->
  <intelishs-page-header
    [title]="isEdit() ? 'Editar Empleado' : 'Registro de Empleado'"
    [subtitle]="
      isEdit()
        ? 'Modifica los datos del empleado seleccionado'
        : 'Administra la información del nuevo empleado, configurando sus datos personales y laborales'
    "
  />

  <form [formGroup]="form" (submit)="$event.preventDefault()">
    <!-- ═══ Stepper (solo en alta) ═══ -->
    @if (!isEdit()) {
      <nz-steps [nzCurrent]="paso() - 1" nzSize="small" style="margin-bottom: 22px">
        <nz-step nzTitle="Datos principales" nzDescription="Se crea el registro" />
        <nz-step nzTitle="Información complementaria" nzDescription="Se completa el resto" />
      </nz-steps>
    }

    <!-- ═══════════════ PASO 1 (alta) / siempre visible en edición ═══════════════ -->
    <div [hidden]="!isEdit() && paso() !== 1">
      <!-- ═══ Sección: Información Personal ═══ -->
      <div class="form-section">
        <intelishs-section-header title="Información Personal" />

        <div class="form-grid">
          <app-form-field
            label="ID Interno (clave)"
            control="idInterno"
            [required]="true"
            [errores]="{
              required: 'El ID interno es requerido',
              idInternoTaken: 'Este ID ya está en uso por otro empleado',
            }"
            [submitted]="submitted()"
          >
            <input
              nz-input
              formControlName="idInterno"
              placeholder="Ej: MC0001"
              [maxlength]="max.idInterno"
            />
            <!-- Va en el slot de ayuda y no en `hint` porque tiene que convivir
                 con el error de duplicado: la sugerencia del siguiente MC####
                 libre solo sirve mientras el mensaje de "ya está en uso" sigue
                 en pantalla. -->
            <span slot="ayuda" class="form-field-ayuda">
              @if (idInternoPendiente) {
                <span class="field-hint">Verificando disponibilidad...</span>
              } @else if (idInternoDuplicado) {
                @if (siguienteIdSugerido()) {
                  <span class="field-hint">
                    Sugerencia disponible:
                    <button
                      type="button"
                      class="suggestion-link"
                      (click)="aplicarSugerenciaIdInterno()"
                    >
                      {{ siguienteIdSugerido() }}
                    </button>
                  </span>
                } @else if (loadingSugerencia()) {
                  <span class="field-hint">Buscando sugerencia disponible...</span>
                }
              }
            </span>
          </app-form-field>

          <app-form-field
            label="Nombre(s)"
            control="nombre"
            [required]="true"
            [errores]="{
              required: 'El nombre es requerido',
              pattern: 'Solo letras, espacios, apóstrofo o guión',
            }"
            [submitted]="submitted()"
          >
            <input
              nz-input
              formControlName="nombre"
              placeholder="Ej: Juan Carlos"
              [maxlength]="max.nombre"
            />
          </app-form-field>

          <app-form-field
            label="Primer Apellido"
            control="primerApellido"
            [required]="true"
            [errores]="{
              required: 'El primer apellido es requerido',
              pattern: 'Solo letras, espacios, apóstrofo o guión',
            }"
            [submitted]="submitted()"
          >
            <input
              nz-input
              formControlName="primerApellido"
              placeholder="Ej: García"
              [maxlength]="max.nombre"
            />
          </app-form-field>

          <app-form-field
            label="Segundo Apellido"
            control="segundoApellido"
            [errores]="{ pattern: 'Solo letras, espacios, apóstrofo o guión' }"
            [submitted]="submitted()"
          >
            <input
              nz-input
              formControlName="segundoApellido"
              placeholder="Ej: López (opcional)"
              [maxlength]="max.nombre"
            />
          </app-form-field>

          <app-form-field
            label="NSS"
            control="nss"
            [errores]="{ pattern: 'El NSS debe tener exactamente 11 dígitos' }"
            [submitted]="submitted()"
          >
            <input
              nz-input
              formControlName="nss"
              placeholder="11 dígitos"
              [maxlength]="max.nss"
              inputmode="numeric"
            />
          </app-form-field>

          <app-form-field
            label="RFC"
            control="rfc"
            [errores]="{ pattern: 'Formato de RFC inválido (Ej: XAXX010101000)' }"
            [submitted]="submitted()"
          >
            <input
              nz-input
              formControlName="rfc"
              placeholder="Ej: XAXX010101000"
              [maxlength]="max.rfc"
            />
          </app-form-field>

          <app-form-field
            label="CURP"
            control="curp"
            [errores]="{ pattern: 'Formato de CURP inválido (18 caracteres)' }"
            [submitted]="submitted()"
          >
            <input
              nz-input
              formControlName="curp"
              placeholder="18 caracteres"
              [maxlength]="max.curp"
            />
          </app-form-field>

          <app-form-field label="Sexo" control="sexoId">
            <nz-select
              formControlName="sexoId"
              nzPlaceHolder="Seleccione Sexo"
              nzShowSearch
              nzAllowClear
              [nzLoading]="loadingCatalogos()"
            >
              @for (g of generos(); track g.id) {
                <nz-option [nzValue]="g.id" [nzLabel]="g.descripcion" />
              }
            </nz-select>
          </app-form-field>

          <app-form-field label="Estado Civil" control="estadoCivilId">
            <nz-select
              formControlName="estadoCivilId"
              nzPlaceHolder="Seleccione Estado Civil"
              nzShowSearch
              nzAllowClear
              [nzLoading]="loadingCatalogos()"
            >
              @for (ec of estadosCiviles(); track ec.id) {
                <nz-option [nzValue]="ec.id" [nzLabel]="ec.descripcion" />
              }
            </nz-select>
          </app-form-field>

          <app-form-field label="Nacionalidad" control="nacionalidadId">
            <nz-select
              formControlName="nacionalidadId"
              nzPlaceHolder="Seleccione Nacionalidad"
              nzShowSearch
              nzAllowClear
              [nzLoading]="loadingCatalogos()"
            >
              @for (n of nacionalidades(); track n.id) {
                <nz-option [nzValue]="n.id" [nzLabel]="n.descripcion" />
              }
            </nz-select>
          </app-form-field>
        </div>

        <!-- Correo + teléfono viven en el FormGroup domicilio, pero el SP los
             necesita en el alta (scorreo / arrtelefonos), así que se muestran
             aquí en el Paso 1. -->
        <div class="form-grid" formGroupName="domicilio">
          <app-form-field
            label="Email"
            control="email"
            [required]="true"
            [errores]="{ required: 'El email es requerido', email: 'Formato de email inválido' }"
            [submitted]="submitted()"
          >
            <input
              nz-input
              formControlName="email"
              placeholder="correo@ejemplo.com"
              [maxlength]="max.email"
            />
          </app-form-field>

          <app-form-field
            label="Teléfono"
            control="telefono"
            [errores]="{ pattern: 'El teléfono debe tener exactamente 10 dígitos' }"
            [submitted]="submitted()"
          >
            <input
              nz-input
              formControlName="telefono"
              placeholder="10 dígitos"
              [maxlength]="max.telefono"
              inputmode="numeric"
            />
          </app-form-field>
        </div>
      </div>

      <!-- ═══ Sección: Asignación y Pago (datos que consume el SP) ═══ -->
      <!-- NOTA: areaId y direccionId son top-level en el FormGroup, NO dentro de datosLaborales -->
      <div class="form-section">
        <intelishs-section-header title="Asignación y Pago" />

        <div class="form-grid">
          <app-form-field
            label="Dirección"
            control="direccionId"
            [required]="true"
            [errores]="{ required: 'La dirección es requerida' }"
            [submitted]="submitted()"
          >
            <nz-select
              formControlName="direccionId"
              nzPlaceHolder="Seleccione Dirección"
              nzShowSearch
              [nzLoading]="loadingCatalogos()"
            >
              @for (d of direcciones(); track d.id) {
                <nz-option [nzValue]="d.id" [nzLabel]="d.descripcion" />
              }
            </nz-select>
          </app-form-field>

          <app-form-field
            label="Área"
            control="areaId"
            [required]="true"
            [errores]="{ required: 'El área es requerida' }"
            [submitted]="submitted()"
          >
            <nz-select
              formControlName="areaId"
              nzPlaceHolder="Seleccione Área"
              nzShowSearch
              [nzLoading]="loadingCatalogos()"
            >
              @for (a of areas(); track a.id) {
                <nz-option [nzValue]="a.id" [nzLabel]="a.descripcion" />
              }
            </nz-select>
          </app-form-field>

          <ng-container formGroupName="datosLaborales">
            <app-form-field label="Puesto" control="puestoId">
              <nz-select
                formControlName="puestoId"
                nzPlaceHolder="Seleccione un puesto"
                nzShowSearch
                nzAllowClear
                [nzLoading]="loadingCatalogos()"
              >
                @for (p of puestos(); track p.id) {
                  <nz-option [nzValue]="p.id" [nzLabel]="p.descripcion" />
                }
              </nz-select>
            </app-form-field>
          </ng-container>

          <app-form-field label="Director / Superior" control="superiorId">
            <nz-select
              formControlName="superiorId"
              nzPlaceHolder="Seleccione director"
              nzShowSearch
              nzAllowClear
              [nzLoading]="loadingCatalogos()"
            >
              @for (d of directores(); track d.directorId) {
                <nz-option
                  [nzValue]="d.directorId"
                  [nzLabel]="d.directorNombre + ' (' + d.directorIdInterno + ')'"
                />
              }
            </nz-select>
          </app-form-field>
        </div>

        <div class="form-grid" formGroupName="datosLaborales">
          <app-form-field label="Periodo de Pago" control="periodoPagoId">
            <nz-select
              formControlName="periodoPagoId"
              nzPlaceHolder="Seleccione Periodo de Pago"
              nzShowSearch
              nzAllowClear
              [nzLoading]="loadingCatalogos()"
            >
              @for (pp of periodosPago(); track pp.id) {
                <nz-option [nzValue]="pp.id" [nzLabel]="pp.descripcion" />
              }
            </nz-select>
          </app-form-field>

          <app-form-field label="Esquema de Pago" control="esquemaPagoId">
            <nz-select
              formControlName="esquemaPagoId"
              nzPlaceHolder="Seleccione Esquema de Pago"
              nzShowSearch
              nzAllowClear
              [nzLoading]="loadingCatalogos()"
            >
              @for (ep of esquemasPago(); track ep.id) {
                <nz-option [nzValue]="ep.id" [nzLabel]="ep.descripcion" />
              }
            </nz-select>
          </app-form-field>
        </div>
      </div>
    </div>

    <!-- ═══════════════ PASO 2 (alta) / siempre visible en edición ═══════════════ -->
    <div [hidden]="!isEdit() && paso() !== 2">
      <!-- ═══ Sección: Datos Laborales ═══ -->
      <div class="form-section">
        <intelishs-section-header title="Datos Laborales" />

        <div class="form-grid">
          <app-form-field label="Fecha de Ingreso" control="fechaIngreso">
            <nz-date-picker
              formControlName="fechaIngreso"
              nzFormat="dd/MM/yyyy"
              nzPlaceHolder="dd/MM/yyyy"
              [nzInputReadOnly]="false"
              (keydown.enter)="commitDateOnEnter(form.get('fechaIngreso'), $event)"
            />
          </app-form-field>
        </div>

        <div class="form-grid" formGroupName="datosLaborales">
          <app-form-field label="Tipo de Contrato" control="tipoContrato">
            <nz-select
              formControlName="tipoContrato"
              nzPlaceHolder="Seleccione Tipo de Contrato"
              nzShowSearch
              nzAllowClear
              [nzLoading]="loadingCatalogos()"
            >
              @for (tc of tiposContrato(); track tc.id) {
                <nz-option [nzValue]="tc.descripcion" [nzLabel]="tc.descripcion" />
              }
            </nz-select>
          </app-form-field>

          <app-form-field label="Duración de Contrato" control="duracionContrato">
            <input
              nz-input
              formControlName="duracionContrato"
              placeholder="Ej: 1 año"
              [maxlength]="max.duracionContrato"
            />
          </app-form-field>

          <app-form-field label="Horario de Trabajo" control="horaInicio">
            <div class="form-time-range">
              <nz-time-picker
                formControlName="horaInicio"
                nzFormat="HH:mm"
                nzPlaceHolder="Inicio"
                class="form-time"
              />
              <span class="form-time-sep">—</span>
              <nz-time-picker
                formControlName="horaFin"
                nzFormat="HH:mm"
                nzPlaceHolder="Fin"
                class="form-time"
              />
            </div>
          </app-form-field>

          <app-form-field label="Días de Descanso" control="diasDescansoIds">
            <nz-select
              formControlName="diasDescansoIds"
              nzMode="multiple"
              nzPlaceHolder="Seleccione uno o varios días"
            >
              @for (d of diasSemana; track d.id) {
                <nz-option [nzValue]="d.id" [nzLabel]="d.label" />
              }
            </nz-select>
          </app-form-field>
        </div>
      </div>

      <!-- ═══ Sección: Datos Salariales ═══ -->
      <!-- Oculta hasta que el DBA exponga columnas (mostrarSeccionesPendientesBd). -->
      @if (mostrarSeccionesPendientesBd) {
        <div class="form-section" formGroupName="datosSalariales">
          <intelishs-section-header title="Datos Salariales" />

          <div class="form-grid">
            <app-form-field label="SD Nominal" control="sdNominal">
              <nz-input-number formControlName="sdNominal" [nzPrecision]="2" nzPlaceHolder="0.00" />
            </app-form-field>

            <app-form-field label="SD Integrado" control="sdIntegrado">
              <nz-input-number
                formControlName="sdIntegrado"
                [nzPrecision]="2"
                nzPlaceHolder="0.00"
              />
            </app-form-field>

            <app-form-field label="Sueldo Neto IMSS" control="sueldoNetoImss">
              <nz-input-number
                formControlName="sueldoNetoImss"
                [nzPrecision]="2"
                nzPlaceHolder="0.00"
              />
            </app-form-field>
          </div>
        </div>
      }

      <!-- ═══ Sección: Domicilio ═══ -->
      <div class="form-section">
        <intelishs-section-header title="Domicilio" />

        <!-- Lugar de nacimiento es top-level pero vive en movempleadodomicilio -->
        <div class="form-grid">
          <app-form-field label="Lugar de Nacimiento" control="lugarNacimiento">
            <input
              nz-input
              formControlName="lugarNacimiento"
              placeholder="Lugar de nacimiento"
              [maxlength]="max.lugarNacimiento"
            />
          </app-form-field>
        </div>

        <div class="form-grid" formGroupName="domicilio">
          <app-form-field label="Calle" control="calle">
            <input nz-input formControlName="calle" placeholder="Calle" [maxlength]="max.calle" />
          </app-form-field>

          <div class="form-field-half">
            <app-form-field label="No. Ext" control="numeroExt" size="xs">
              <input
                nz-input
                formControlName="numeroExt"
                placeholder="Ext"
                [maxlength]="max.numeroCasa"
              />
            </app-form-field>
            <app-form-field label="No. Int" control="numeroInt" size="xs">
              <input
                nz-input
                formControlName="numeroInt"
                placeholder="Int"
                [maxlength]="max.numeroCasa"
              />
            </app-form-field>
          </div>

          <app-form-field label="Colonia" control="colonia">
            <input
              nz-input
              formControlName="colonia"
              placeholder="Colonia"
              [maxlength]="max.colonia"
            />
          </app-form-field>

          <app-form-field label="Población / Municipio" control="poblacion">
            <input
              nz-input
              formControlName="poblacion"
              placeholder="Población"
              [maxlength]="max.poblacion"
            />
          </app-form-field>

          <app-form-field label="Estado" control="estado">
            <nz-select
              formControlName="estado"
              nzPlaceHolder="Seleccione Estado"
              nzShowSearch
              nzAllowClear
              [nzLoading]="loadingCatalogos()"
            >
              @for (e of estadosMx(); track e.id) {
                <nz-option [nzValue]="e.descripcion" [nzLabel]="e.descripcion" />
              }
            </nz-select>
          </app-form-field>

          <app-form-field
            label="Código Postal"
            control="codigoPostal"
            [errores]="{ pattern: 'El C.P. debe tener exactamente 5 dígitos' }"
            [submitted]="submitted()"
          >
            <input
              nz-input
              formControlName="codigoPostal"
              placeholder="5 dígitos"
              [maxlength]="max.codigoPostal"
              inputmode="numeric"
            />
          </app-form-field>
        </div>
      </div>

      <!-- ═══ Sección: Datos Bancarios ═══ -->
      <!-- Oculta hasta que el DBA exponga columnas (mostrarSeccionesPendientesBd). -->
      @if (mostrarSeccionesPendientesBd) {
        <div class="form-section" formGroupName="datosBancarios">
          <intelishs-section-header title="Datos Bancarios" />

          <div class="form-grid">
            <app-form-field label="Banco" control="banco">
              <nz-select
                formControlName="banco"
                nzPlaceHolder="Seleccione un banco"
                nzShowSearch
                nzAllowClear
                [nzLoading]="bancos().length === 0"
              >
                @for (b of bancos(); track b.id) {
                  <nz-option [nzValue]="b.descripcion" [nzLabel]="b.descripcion" />
                }
              </nz-select>
            </app-form-field>

            <app-form-field label="Número de Cuenta" control="numeroCuenta">
              <input
                nz-input
                formControlName="numeroCuenta"
                placeholder="Número de cuenta"
                [maxlength]="max.cuenta"
                inputmode="numeric"
              />
            </app-form-field>

            <app-form-field
              label="CLABE Interbancaria"
              control="clabeInterbancaria"
              [errores]="{ pattern: 'La CLABE debe tener exactamente 18 dígitos' }"
              [submitted]="submitted()"
            >
              <input
                nz-input
                formControlName="clabeInterbancaria"
                placeholder="18 dígitos"
                [maxlength]="max.clabe"
                inputmode="numeric"
              />
            </app-form-field>
          </div>
        </div>
      }

      <!-- ═══ Sección: INFONAVIT ═══ -->
      <!-- Oculta hasta que el DBA exponga columnas (mostrarSeccionesPendientesBd). -->
      @if (mostrarSeccionesPendientesBd) {
        <div class="form-section" formGroupName="infonavit">
          <intelishs-section-header title="INFONAVIT" />

          <div class="form-grid">
            <app-form-field label="Número de Crédito" control="numeroCredito">
              <input
                nz-input
                formControlName="numeroCredito"
                placeholder="Número de crédito"
                [maxlength]="max.credito"
                inputmode="numeric"
              />
            </app-form-field>

            <app-form-field label="Tipo de Retención" control="tipoRetencion">
              <input
                nz-input
                formControlName="tipoRetencion"
                placeholder="Tipo de retención"
                [maxlength]="max.texto"
              />
            </app-form-field>

            <app-form-field label="Importe / VSM / Cuota" control="importeVsmCuota">
              <input
                nz-input
                formControlName="importeVsmCuota"
                placeholder="Importe, VSM o %"
                [maxlength]="max.texto"
              />
            </app-form-field>
          </div>
        </div>
      }

      <!-- ═══ Sección: Pagadoras IMSS ═══ -->
      <div class="form-section array-block array-block--4">
        <intelishs-section-header title="Pagadoras IMSS" />

        <div class="array-add-bar">
          <button type="button" nz-button class="sinay-outline-reset" (click)="agregarPagadora()">
            <nz-icon nzType="plus" /> Agregar Pagadora
          </button>
        </div>

        @if (pagadoras.length > 0) {
          <!-- Los títulos van una vez arriba y no repetidos en cada fila. Es
               decorativo para el lector de pantalla —`aria-hidden`—: la etiqueta
               real sigue en cada campo, oculta visualmente, así que cada control
               se anuncia con su nombre sin que la pantalla lo repita N veces. -->
          <div class="array-head" aria-hidden="true">
            <span>Pagadora</span>
            <span>Fecha Alta IMSS</span>
            <span>Fecha Baja IMSS</span>
            <span>Actual</span>
            <span></span>
          </div>
        }

        @for (pagadora of pagadoras.controls; track $index; let i = $index) {
          <div class="array-row" [formGroupName]="'pagadoras'">
            <ng-container [formGroupName]="i">
              <label class="array-cell">
                <span class="array-label">Pagadora:</span>
                <input
                  nz-input
                  formControlName="pagadora"
                  placeholder="Nombre de la pagadora"
                  [maxlength]="max.texto"
                />
              </label>
              <label class="array-cell">
                <span class="array-label">Fecha Alta IMSS:</span>
                <nz-date-picker
                  formControlName="fechaAltaImss"
                  nzFormat="dd/MM/yyyy"
                  nzPlaceHolder="dd/MM/yyyy"
                  [nzInputReadOnly]="false"
                  (keydown.enter)="commitDateOnEnter(pagadoras.at(i).get('fechaAltaImss'), $event)"
                />
              </label>
              <label class="array-cell">
                <span class="array-label">Fecha Baja IMSS:</span>
                <nz-date-picker
                  formControlName="fechaBajaImss"
                  nzFormat="dd/MM/yyyy"
                  nzPlaceHolder="dd/MM/yyyy"
                  [nzInputReadOnly]="false"
                  (keydown.enter)="commitDateOnEnter(pagadoras.at(i).get('fechaBajaImss'), $event)"
                />
              </label>
              <label class="array-cell array-cell--switch">
                <span class="array-label">Actual:</span>
                <!-- Solo lectura por ahora — el backend deriva esActual de
                     `bactivo` y NO lo aplica en ActualizarPagadoraAsync. Para
                     dar de baja una pagadora usar la acción "Eliminar" (soft-
                     delete). Habilitar este switch requiere implementar la
                     transición de actual ↔ histórico en backend. -->
                <nz-switch formControlName="esActual" nzDisabled />
              </label>
              <intelishs-table-actions
                [row]="i"
                [actions]="accionesPagadora(i)"
                (action)="onAccionPagadora($event, i)"
              />
            </ng-container>
          </div>
        }

        @if (pagadoras.length === 0) {
          <intelishs-empty-state icon="audit" message="No hay pagadoras registradas" />
        }
      </div>

      <!-- ═══ Sección: Contactos de Emergencia ═══ -->
      <div class="form-section array-block array-block--3">
        <intelishs-section-header title="Contactos de Emergencia" />

        <div class="array-add-bar">
          <button type="button" nz-button class="sinay-outline-reset" (click)="agregarContacto()">
            <nz-icon nzType="plus" /> Agregar Contacto
          </button>
        </div>

        @if (contactosEmergencia.length > 0) {
          <div class="array-head" aria-hidden="true">
            <span>Nombre</span>
            <span>Parentesco</span>
            <span>Teléfono</span>
            <span></span>
          </div>
        }

        @for (contacto of contactosEmergencia.controls; track $index; let i = $index) {
          <div class="array-row" [formGroupName]="'contactosEmergencia'">
            <ng-container [formGroupName]="i">
              <label class="array-cell">
                <span class="array-label">Nombre:</span>
                <input
                  nz-input
                  formControlName="nombre"
                  placeholder="Nombre del contacto"
                  [maxlength]="max.nombre"
                />
              </label>
              <label class="array-cell">
                <span class="array-label">Parentesco:</span>
                <!-- Bind por ID: el INSERT en BD necesita fkidparentesco (NOT NULL). -->
                <nz-select
                  formControlName="parentescoId"
                  nzPlaceHolder="Seleccione Parentesco"
                  nzShowSearch
                  nzAllowClear
                  [nzLoading]="loadingCatalogos()"
                >
                  @for (par of parentescos(); track par.id) {
                    <nz-option [nzValue]="par.id" [nzLabel]="par.descripcion" />
                  }
                </nz-select>
              </label>
              <label class="array-cell">
                <span class="array-label">Teléfono:</span>
                <input
                  nz-input
                  formControlName="telefono"
                  placeholder="Teléfono"
                  [maxlength]="max.telefono"
                  inputmode="numeric"
                />
              </label>
              <intelishs-table-actions
                [row]="i"
                [actions]="accionesContacto(i)"
                (action)="onAccionContacto($event, i)"
              />
            </ng-container>
          </div>
        }

        @if (contactosEmergencia.length === 0) {
          <intelishs-empty-state icon="contacts" message="No hay contactos registrados" />
        }
      </div>

      <!-- ═══ Sección: Estudios ═══ -->
      <div class="form-section array-block array-block--2">
        <intelishs-section-header title="Estudios" />

        <div class="array-add-bar">
          <button type="button" nz-button class="sinay-outline-reset" (click)="agregarEstudio()">
            <nz-icon nzType="plus" /> Agregar Estudio
          </button>
        </div>

        @if (estudios.length > 0) {
          <div class="array-head" aria-hidden="true">
            <span>Grado de Estudios</span>
            <span>Campo de Estudio</span>
            <span></span>
          </div>
        }

        @for (estudio of estudios.controls; track $index; let i = $index) {
          <div class="array-row" [formGroupName]="'estudios'">
            <ng-container [formGroupName]="i">
              <label class="array-cell">
                <span class="array-label">Grado de Estudios:</span>
                <!-- Bind por ID: el INSERT en BD necesita fkidgradoestudio (NOT NULL). -->
                <nz-select
                  formControlName="gradoEstudiosId"
                  nzPlaceHolder="Seleccione Grado de Estudios"
                  nzShowSearch
                  nzAllowClear
                  [nzLoading]="loadingCatalogos()"
                >
                  @for (ge of gradosEstudios(); track ge.id) {
                    <nz-option [nzValue]="ge.id" [nzLabel]="ge.descripcion" />
                  }
                </nz-select>
              </label>
              <label class="array-cell">
                <span class="array-label">Campo de Estudio:</span>
                <input
                  nz-input
                  formControlName="campoEstudio"
                  placeholder="Ej: Ingeniería en Sistemas"
                  [maxlength]="max.texto"
                />
              </label>
              <intelishs-table-actions
                [row]="i"
                [actions]="accionesEstudio(i)"
                (action)="onAccionEstudio($event, i)"
              />
            </ng-container>
          </div>
        }

        @if (estudios.length === 0) {
          <intelishs-empty-state icon="file-text" message="No hay estudios registrados" />
        }
      </div>
    </div>
  </form>
  <!-- Barra de acciones al cierre del formulario, afijada al borde inferior.

       Va al final y no arriba porque es donde el patrón corresponde: se captura
       de arriba hacia abajo y se cierra confirmando. Afijada, además, está
       visible desde el primer scroll, así que no hay que volver al inicio para
       guardar —con ~40 campos, eso es una invitación a abandonar la página con
       lo capturado a medias—.

       El `[affix]` del componente envuelve en `nz-affix`, y NO es un
       `position: fixed` a mano: affix mide el hueco que la barra ocupa en el
       flujo (`placeholderNode.offsetWidth`) y le aplica ese ancho y ese offset
       izquierdo, recalculando en cada resize. Un `fixed` propio no puede: sus
       porcentajes se resuelven contra el viewport, no contra el área de
       contenido, así que la barra se pasaba del borde derecho por el ancho del
       sidebar (260px) y se llevaba los botones fuera de cuadro. Affix también
       deja un placeholder del mismo alto en el flujo, así que el final del
       formulario no queda tapado y no hace falta `padding-bottom`.

       `position: sticky` no era opción: el shell lo rompe —ver "Sin barras
       sticky" en CLAUDE.md—. Affix se salva porque termina en `fixed`.

       Sin `[target]`: affix vigila la ventana, que es donde ocurre el scroll
       real —`.app-content` del shell declara `overflow: auto` pero nunca
       scrollea, ver la misma sección—. -->
  <intelishs-page-actions variant="bar" align="end" [affix]="true" [offsetBottom]="12">
    @if (isEdit()) {
      <button nz-button class="sinay-outline-reset" (click)="cancelar()">Cancelar</button>
      <button nz-button class="btn-add" [nzLoading]="saving()" (click)="guardar()">
        Actualizar
      </button>
    } @else if (paso() === 1) {
      <button nz-button class="sinay-outline-reset" (click)="cancelar()">Cancelar</button>
      <button nz-button class="btn-add" [nzLoading]="saving()" (click)="crearPaso1()">
        Crear y continuar
      </button>
    } @else {
      <button nz-button class="sinay-outline-reset" (click)="cancelar()">Finalizar después</button>
      <button nz-button class="btn-add" [nzLoading]="saving()" (click)="guardar()">
        Guardar y finalizar
      </button>
    }
  </intelishs-page-actions>
</div>
